import React, { useState } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "VALIDMAIL transformed our email deliverability overnight. We went from constant bounce issues to a clean, healthy list that actually reaches our customers.",
    metric: "54%",
    metricLabel: "reduction in bounce rate within the first month",
    author: "Sarah Chen",
    role: "Head of Marketing",
    company: "TechFlow Solutions",
    avatar: "SC",
  },
  {
    quote:
      "The API integration was seamless. Our dev team had it running in production within 2 hours. Now every signup is validated in real-time.",
    metric: "8% → 1.5%",
    metricLabel: "bounce rate improvement on signup forms",
    author: "Michael Rodriguez",
    role: "CTO",
    company: "CloudSync",
    avatar: "MR",
  },
  {
    quote:
      "As an agency managing 20+ clients, VALIDMAIL saves us hours every week. Bulk validation is fast, accurate, and the reports are perfect for client presentations.",
    metric: "12 clients",
    metricLabel: "deliverability restored in 30 days",
    author: "Emma Thompson",
    role: "Agency Director",
    company: "GrowthLab Digital",
    avatar: "ET",
  },
  {
    quote:
      "Finally, an email validation tool that understands compliance. GDPR and DPDP support gave us confidence to expand into European and Indian markets.",
    metric: "99.2%",
    metricLabel: "inbox placement rate achieved",
    author: "Raj Patel",
    role: "VP of Product",
    company: "DataFlow Inc",
    avatar: "RP",
  },
];

const STATS = [
  { value: "2.5M+", label: "Emails validated daily" },
  { value: "4.9/5", label: "Average customer rating" },
  { value: "98%+", label: "Customer retention rate" },
  { value: "< 2hrs", label: "Average integration time" },
];

export default function SocialProofSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setActiveIndex((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  const activeTestimonial = TESTIMONIALS[activeIndex];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Teams who validate with VALIDMAIL{" "}
            <span className="text-sky-600 dark:text-sky-400">
              see fewer bounces in days
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what real teams are saying
            about their results.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mb-16">
          <div className="relative bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-12 border-2 border-slate-200 dark:border-slate-800 shadow-2xl">
            <div className="absolute -top-6 left-8 w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Quote className="h-6 w-6 text-white" />
            </div>

            <div className="flex items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="h-5 w-5 fill-amber-400 text-amber-400"
                />
              ))}
            </div>

            <blockquote className="text-xl md:text-2xl text-slate-800 dark:text-slate-100 leading-relaxed mb-8 font-medium">
              "{activeTestimonial.quote}"
            </blockquote>

            <div className="bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-2xl p-6 mb-8 border border-sky-200 dark:border-sky-800">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-sky-600 dark:text-sky-400 mb-2">
                  {activeTestimonial.metric}
                </div>
                <div className="text-sm md:text-base text-slate-600 dark:text-slate-300">
                  {activeTestimonial.metricLabel}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md">
                  {activeTestimonial.avatar}
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-slate-50">
                    {activeTestimonial.author}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    {activeTestimonial.role} at {activeTestimonial.company}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </button>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </button>
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {TESTIMONIALS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === activeIndex
                      ? "w-8 bg-sky-600 dark:bg-sky-400"
                      : "w-2 bg-slate-300 dark:bg-slate-700"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 text-center hover:border-sky-300 dark:hover:border-sky-700 transition-all hover:shadow-lg"
            >
              <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          {/* <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200">
            Read more customer stories
          </button> */}
        </div>
      </div>
    </section>
  );
}
